import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { AppDispatch, RootState } from '../../store';
import { Workflow, loadWorkflows } from '../../store/project/projectSlice';
import * as repo from '../../services/repo';
import type { CanvasDoc } from '../../services/canvasSchema';
import { buildFolderTree, compareNames, isInside, listFolderPaths } from '../../utils/workflowFolders';
import { showAlert, showConfirm } from '../common/AppDialog';
import { typeLabelOf } from '../workflow/nodeCatalog';
import { TOOLBOX_CATEGORIES, TOOLBOX_NODE_TYPES } from '../workflow/panels/ToolboxPanel/ToolClassPanel';
import { useTranslation } from 'react-i18next';
import { LINKED_FIELDS, linkedDefinition } from '../workflow/utils/definitionRefs';
import { usePersistentState } from '../../utils/usePersistentState';

/** 노드 유형 표시 이름 — 없는 유형은 타입 문자열 그대로 */
const typeLabel = (ty: string) => typeLabelOf(ty);

/** 값이 한 칸에 보일 수 있는 것인가 — 배열·객체(매핑·컬럼 목록 등)는 일괄 수정 대상으로 삼지 않는다 */
const isScalar = (v: any) => v === null || ['string', 'number', 'boolean'].includes(typeof v);

/** 바꾼 값을 원래 자료형에 맞춘다 — 'true' 를 넣었다고 문자열이 되면 그 노드는 설정이 깨진다 */
function coerce(original: any, next: string): any {
  if (typeof original === 'boolean') return next === 'true' || next === '1';
  if (typeof original === 'number') { const n = Number(next); return Number.isFinite(n) ? n : original; }
  return next;
}

interface Row {
  /** workflowId/노드순번/출처:속성 — 노드 id 가 겹쳐도 유일하다 */
  key: string;
  workflow: Workflow;
  projectName: string;
  /** 문서 안 노드 순번 — 저장할 때 이 자리의 노드를 고친다 (id 중복에 흔들리지 않게) */
  nodeIndex: number;
  nodeId: string;
  nodeLabel: string;
  nodeType: string;
  /** 'label' = 노드 이름, 'config' = 설정 값 */
  src: 'label' | 'config';
  attr: string;
  value: any;
}

interface Filters {
  /** '프로젝트id\u0001폴더경로' — 여러 프로젝트를 고르면 폴더 이름이 겹칠 수 있어 프로젝트까지 붙인다 */
  folder: string;
  nameLike: string;
  nodeType: string;
  attr: string;
  valueLike: string;
  valueExact: boolean;
}

const EMPTY: Filters = { folder: '', nameLike: '', nodeType: '', attr: '', valueLike: '', valueExact: false };
const SEP = '\u0001';

/** 읽어 둔 캔버스와 그때의 버전 — 버전이 바뀌었으면 다시 읽는다 */
interface CachedDoc { doc: CanvasDoc; ver: number }

/**
 * 속성 검색·일괄 수정 — 여러 워크플로(여러 프로젝트 가능)의 노드 속성을 한 번에 찾아 바꾸고,
 * 바뀐 워크플로만 새 버전으로 저장한다.
 *
 *   · 위쪽 리본에서 프로젝트를 켜고 끈다 — 처음에는 지금 프로젝트만 (로그인 사용자가 볼 수 있는 프로젝트 전부가 후보)
 *   · 화면을 열면 고른 프로젝트의 캔버스를 곧바로 읽는다 — 노드 유형·속성 목록이 검색 전부터 채워진다
 *   · 닫았다 다시 열면 닫기 전 상태(조건·결과·선택·입력값)가 그대로다 (utils/usePersistentState)
 *   · 노드 이름은 노드(label)와 설정(config.label) 두 곳에 같은 값으로 들어 있다 — 한 줄로 보이고 둘을 함께 고친다
 *
 * 저장은 워크플로마다 새 버전이다. 지금 열린 워크플로를 바꿨으면 창을 닫을 때 새로고침을 묻는다(openWorkflow).
 * 배포된 DAG 는 바뀌지 않는다 — 반영하려면 디자이너에서 다시 Deploy 한다.
 */
const BulkEditScreen: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const selectedProjectId = useSelector((s: RootState) => s.project.selectedProjectId);
  const projects = useSelector((s: RootState) => s.project.projects);
  const storeWorkflows = useSelector((s: RootState) => s.project.workflows);
  const usrid = useSelector((s: RootState) => s.auth.user?.usrid || '');
  const viewOnly = useSelector((s: RootState) => s.auth.user?.viewOnly === true);
  const connections = useSelector((s: RootState) => s.connections.list);

  // ── 닫아도 남는 상태
  const [projectIds, setProjectIds] = usePersistentState<string[]>('bulk:projects', () => (selectedProjectId ? [selectedProjectId] : []));
  const [otherLists, setOtherLists] = usePersistentState<Record<string, Workflow[]>>('bulk:otherLists', {});
  const [docs, setDocs] = usePersistentState<Record<string, CachedDoc>>('bulk:docs', {});
  const [filters, setFilters] = usePersistentState<Filters>('bulk:filters', EMPTY);
  const [searched, setSearched] = usePersistentState('bulk:searched', false);
  const [checked, setChecked] = usePersistentState<Record<string, boolean>>('bulk:checked', {});
  const [editMode, setEditMode] = usePersistentState<'set' | 'replace'>('bulk:editMode', 'set');
  const [newValue, setNewValue] = usePersistentState('bulk:newValue', '');
  const [findText, setFindText] = usePersistentState('bulk:findText', '');
  const [replaceText, setReplaceText] = usePersistentState('bulk:replaceText', '');
  const [result, setResult] = usePersistentState<string[]>('bulk:result', []);
  // 창이 열려 있는 동안만
  const [scan, setScan] = React.useState<{ busy: boolean; done: number; total: number; error: string }>({ busy: false, done: 0, total: 0, error: '' });
  const [saving, setSaving] = React.useState(false);

  const projectName = useCallback((pid: string) => projects.find((p) => p.id === pid)?.name || pid, [projects]);
  const multi = projectIds.length > 1;

  // 지금 프로젝트는 스토어 목록(항상 최신), 다른 프로젝트는 여기서 읽어 둔 목록
  const pool: Workflow[] = useMemo(() => projectIds.flatMap((pid) => (pid === selectedProjectId
    ? storeWorkflows.filter((w) => w.projectId === pid)
    : (otherLists[pid] || [])))
    .slice()
    .sort((a, b) => compareNames(projectName(a.projectId), projectName(b.projectId))
      || compareNames(a.folder || '', b.folder || '') || compareNames(a.name, b.name)),
  [projectIds, selectedProjectId, storeWorkflows, otherLists, projectName]);

  const folderOptions = useMemo(() => projectIds.flatMap((pid) => {
    const list = pool.filter((w) => w.projectId === pid);
    return listFolderPaths(buildFolderTree(list)).map((f) => ({ value: `${pid}${SEP}${f}`, label: multi ? `${projectName(pid)} / ${f}` : f }));
  }), [projectIds, pool, multi, projectName]);

  const targets = useMemo(() => {
    if (!filters.folder) return pool;
    const [pid, path] = filters.folder.split(SEP);
    return pool.filter((w) => w.projectId === pid && isInside(w.folder, path));
  }, [pool, filters.folder]);

  // ── 다른 프로젝트의 워크플로 목록 — 켤 때와 창을 열 때 새로 읽는다(버전이 바뀌었을 수 있다)
  const listFetched = useRef<Set<string>>(new Set());
  useEffect(() => {
    projectIds.filter((pid) => pid !== selectedProjectId && !listFetched.current.has(pid)).forEach((pid) => {
      listFetched.current.add(pid);
      repo.listWorkflows(pid)
        .then((list) => setOtherLists((prev) => ({ ...prev, [pid]: list })))
        .catch((e) => setScan((p) => ({ ...p, error: `${projectName(pid)}: ${(e as Error).message}` })));
    });
  }, [projectIds, selectedProjectId, setOtherLists, projectName]);

  // ── 캔버스 읽기 — 없거나 버전이 바뀐 것만. 창을 열면 곧바로 돌아 노드 유형·속성 목록을 채운다
  const docsRef = useRef(docs);
  docsRef.current = docs;
  const scanning = useRef(false);
  const scanCanvases = useCallback(async (list: Workflow[]) => {
    const need = list.filter((w) => (w.curVerNo ?? 0) >= 1 && docsRef.current[w.id]?.ver !== (w.curVerNo ?? 0));
    if (!need.length || scanning.current) return;
    scanning.current = true;
    setScan({ busy: true, done: 0, total: need.length, error: '' });
    const next: Record<string, CachedDoc> = { ...docsRef.current };
    let done = 0;
    for (const w of need) {
      try {
        const doc = await repo.loadCanvas(w.id, w.curVerNo ?? 0);
        if (doc) next[w.id] = { doc, ver: w.curVerNo ?? 0 };
      } catch (e) {
        setScan((p) => ({ ...p, error: `${w.name}: ${(e as Error).message}` }));
      }
      done += 1;
      const n = done;
      setScan((p) => ({ ...p, done: n }));
    }
    setDocs(next);
    setScan((p) => ({ ...p, busy: false }));
    scanning.current = false;
  }, [setDocs]);
  useEffect(() => { scanCanvases(pool); }, [pool, scanCanvases]);

  /**
   * 노드 목록은 **툴박스와 같은 묶음·순서**(데이터·프로세스·컨트롤·그룹·실행·기타)로 — 읽기 전에도 고를 수 있다.
   * 툴박스에 없는 유형(예전 캔버스의 옛 노드 등)이 캔버스에 있으면 맨 끝 「그 밖」 에 붙인다.
   * 속성 목록은 읽은 캔버스에서 모은다.
   */
  const { extraTypes, attrOptions } = useMemo(() => {
    const known = new Set(TOOLBOX_NODE_TYPES.map((e) => e.type));
    const extra = new Set<string>();
    const attrs = new Set<string>(['label']);
    pool.forEach((w) => (docs[w.id]?.doc.nodes || []).forEach((n: any) => {
      const ty = String(n.type || '');
      if (ty && !known.has(ty)) extra.add(ty);
      Object.entries((n.data?.config || {}) as Record<string, any>).forEach(([k, v]) => { if (k !== 'label' && isScalar(v)) attrs.add(k); });
    }));
    return { extraTypes: Array.from(extra).sort(), attrOptions: Array.from(attrs).sort() };
  }, [pool, docs]);

  const rows: Row[] = useMemo(() => {
    if (!searched) return [];
    const out: Row[] = [];
    const like = filters.valueLike.trim();
    const nameLike = filters.nameLike.trim();
    for (const w of targets) {
      if (nameLike && !w.name.toLowerCase().includes(nameLike.toLowerCase())) continue;
      const doc = docs[w.id]?.doc;
      if (!doc) continue;
      ((doc.nodes || []) as any[]).forEach((n, nodeIndex) => {
        if (filters.nodeType && n.type !== filters.nodeType) return;
        const entries: Array<{ attr: string; value: any; src: 'label' | 'config' }> = [
          { attr: 'label', value: n.data?.label, src: 'label' },
          ...Object.entries((n.data?.config || {}) as Record<string, any>)
            // config.label 은 노드 이름과 같은 값이다 — 따로 보이면 같은 노드가 두 줄이 된다(현장 2026-09-28)
            .filter(([k]) => k !== 'label')
            // 데이터 정의를 가리키는 노드의 경로·연결·테이블은 정의가 주인이다 — 여기서 고쳐도 저장되지 않는다
            .filter(([k, v]) => isScalar(v) && !(linkedDefinition(n) && (LINKED_FIELDS as any)[n.type]?.includes(k)))
            .map(([attr, value]) => ({ attr, value, src: 'config' as const })),
        ];
        for (const { attr, value, src } of entries) {
          if (filters.attr && attr !== filters.attr) continue;
          if (!isScalar(value)) continue;
          const s = value === null || value === undefined ? '' : String(value);
          if (like && (filters.valueExact ? s !== like : !s.toLowerCase().includes(like.toLowerCase()))) continue;
          out.push({
            key: `${w.id}/${nodeIndex}/${src}:${attr}`,
            workflow: w, projectName: projectName(w.projectId), nodeIndex, nodeId: n.id,
            nodeLabel: String(n.data?.label || n.id), nodeType: String(n.type || ''),
            src, attr, value,
          });
        }
      });
    }
    return out;
  }, [searched, targets, docs, filters, projectName]);

  const selected = rows.filter((r) => checked[r.key]);

  /** 바꾼 뒤의 값 — 미리보기와 저장이 같은 계산을 쓴다 */
  const nextValueOf = useCallback((r: Row): any => {
    if (editMode === 'set') return coerce(r.value, newValue);
    const s = r.value === null || r.value === undefined ? '' : String(r.value);
    if (!findText) return r.value;
    return coerce(r.value, s.split(findText).join(replaceText));
  }, [editMode, newValue, findText, replaceText]);

  const changes = useMemo(
    () => selected.map((r) => ({ row: r, next: nextValueOf(r) })).filter((c) => String(c.next) !== String(c.row.value)),
    [selected, nextValueOf],
  );

  const toggleProject = (pid: string) => {
    listFetched.current.delete(pid);                  // 다시 켜면 목록을 새로 읽는다
    setProjectIds((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]));
    setFilters((f) => ({ ...f, folder: '' }));
    setChecked({});
  };

  const runSearch = async () => {
    setResult([]);
    await scanCanvases(targets);
    setChecked({});
    setSearched(true);
  };

  /** 저장 — 바뀐 워크플로마다 새 버전으로 */
  const applyAndSave = async () => {
    if (!changes.length) return;
    const byWorkflow = new Map<string, typeof changes>();
    changes.forEach((c) => {
      const arr = byWorkflow.get(c.row.workflow.id) || [];
      arr.push(c);
      byWorkflow.set(c.row.workflow.id, arr);
    });
    const ok = await showConfirm(
      t('bulk:confirm', { workflows: byWorkflow.size, n: changes.length }),
      { title: t('bulk:confirmTitle'), okText: t('bulk:save') },
    );
    if (!ok) return;

    setSaving(true);
    const log: string[] = [];
    const nextDocs: Record<string, CachedDoc> = { ...docs };
    const touchedProjects = new Set<string>();
    for (const [workflowId, list] of Array.from(byWorkflow.entries())) {
      const w = pool.find((x) => x.id === workflowId)!;
      const cached = nextDocs[workflowId];
      if (!cached) { log.push(t('bulk:noCanvas', { name: w.name })); continue; }
      const doc = cached.doc;
      if (doc.migration?.tooNew) { log.push(t('bulk:tooNew', { name: w.name })); continue; }
      // 노드를 복사하면서 바꾼다 — 원본 배열을 건드리지 않아야 실패해도 화면이 어긋나지 않는다.
      const edits = new Map(list.map((c) => [`${c.row.nodeIndex}/${c.row.src}:${c.row.attr}`, c.next]));
      const nodes = (doc.nodes as any[]).map((n, nodeIndex) => {
        const label = edits.get(`${nodeIndex}/label:label`);
        const cfg = { ...(n.data?.config || {}) };
        let touched = label !== undefined;
        // 이름은 설정에도 같은 값이 있다 — 함께 바꿔야 속성 패널·변환기가 같은 이름을 본다
        if (label !== undefined && 'label' in cfg) cfg.label = label;
        Object.keys(cfg).forEach((k) => {
          if (k === 'label') return;
          const v = edits.get(`${nodeIndex}/config:${k}`);
          if (v !== undefined) { cfg[k] = v; touched = true; }
        });
        if (!touched) return n;
        return { ...n, data: { ...(n.data || {}), ...(label !== undefined ? { label } : {}), config: cfg } };
      });
      try {
        // 스토어 thunk 가 아니라 저장소를 바로 쓴다 — 다른 프로젝트 워크플로도 저장하고, 지금 열린 캔버스의
        // '읽은 버전' 을 올려 버리지 않게(그래야 창을 닫을 때 새 버전이 있다고 알아챈다)
        const ver = await repo.saveCanvas(workflowId, w.curVerNo ?? 0, { nodes, edges: doc.edges as any, settings: doc.settings }, usrid);
        nextDocs[workflowId] = { doc: { ...doc, nodes } as CanvasDoc, ver };
        touchedProjects.add(w.projectId);
        if (w.projectId !== selectedProjectId) {
          setOtherLists((prev) => ({ ...prev, [w.projectId]: (prev[w.projectId] || []).map((x) => (x.id === workflowId ? { ...x, curVerNo: ver } : x)) }));
        }
        log.push(t('bulk:saved', { name: w.name, n: list.length, ver }));
      } catch (e) {
        log.push(`✗ ${w.name}: ${(e as Error).message}`);
      }
    }
    setDocs(nextDocs);
    // 지금 프로젝트의 목록(버전)을 새로 받는다 — 열려 있거나 캐시된 캔버스가 새 버전을 알아챈다
    if (selectedProjectId && touchedProjects.has(selectedProjectId)) await dispatch(loadWorkflows(selectedProjectId));
    setChecked({});
    setResult(log);
    setSaving(false);
    if (log.some((l) => l.startsWith('✗'))) showAlert(log.join('\n'), { title: t('bulk:partialFail'), tone: 'warning' });
  };

  const inputCls = 'border border-gray-300 rounded px-2 py-1 text-xs bg-white';

  if (!selectedProjectId) {
    return <div className="p-6 text-sm text-gray-500">{t('bulk:selectProject')}</div>;
  }

  return (
    <div className="flex flex-col h-full w-full bg-gray-50 p-6 overflow-auto">
      <div className="text-xs text-gray-500 mb-3">{t('bulk:intro')}</div>

      {/* ── 프로젝트 리본 — 켜고 끈다. 처음에는 지금 프로젝트만 */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <span className="text-xs font-semibold text-gray-600 mr-1">{t('bulk:projects')}</span>
        {projects.map((p) => {
          const on = projectIds.includes(p.id);
          return (
            <button key={p.id} type="button" onClick={() => toggleProject(p.id)} aria-pressed={on}
              className={classNames('px-3 py-1 text-xs rounded-full border transition-colors',
                on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100')}>
              {p.name}{p.id === selectedProjectId ? ` ${t('bulk:current')}` : ''}
            </button>
          );
        })}
      </div>

      {/* ── 검색 조건 */}
      <div className="bg-white rounded-lg shadow-md p-3 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-gray-600">{t('bulk:folder')}
            <select className={inputCls} value={filters.folder} onChange={(e) => setFilters({ ...filters, folder: e.target.value })}>
              <option value="">{t('bulk:allCount', { n: pool.length })}</option>
              {folderOptions.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-600">{t('bulk:nameLike')}
            <input className={inputCls} value={filters.nameLike} onChange={(e) => setFilters({ ...filters, nameLike: e.target.value })} placeholder={t('bulk:nameLikePh')} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-600">{t('bulk:nodeType')}
            <select className={inputCls} value={filters.nodeType} onChange={(e) => setFilters({ ...filters, nodeType: e.target.value })}>
              <option value="">{t('bulk:all')}</option>
              {TOOLBOX_CATEGORIES.map((c) => (
                <optgroup key={c} label={t(`workflow:toolbox.${c}`)}>
                  {TOOLBOX_NODE_TYPES.filter((e) => e.category === c).map((e) => <option key={e.type} value={e.type}>{typeLabel(e.type)}</option>)}
                </optgroup>
              ))}
              {extraTypes.length > 0 && (
                <optgroup label={t('bulk:otherTypes')}>
                  {extraTypes.map((ty) => <option key={ty} value={ty}>{typeLabel(ty)} ({ty})</option>)}
                </optgroup>
              )}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-600">{t('bulk:attr')}
            <select className={inputCls} value={filters.attr} onChange={(e) => setFilters({ ...filters, attr: e.target.value })}>
              <option value="">{t('bulk:all')}</option>
              {attrOptions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-600">{t('bulk:value')}
            <input className={inputCls} value={filters.valueLike} onChange={(e) => setFilters({ ...filters, valueLike: e.target.value })} placeholder={t('bulk:valuePh')} />
          </label>
          <label className="flex items-center gap-1 text-xs text-gray-600 pb-1">
            <input type="checkbox" checked={filters.valueExact} onChange={(e) => setFilters({ ...filters, valueExact: e.target.checked })} />
            {t('bulk:exact')}
          </label>
          <button className="px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" onClick={runSearch} disabled={scan.busy}>
            {t('bulk:search')}
          </button>
          <button className="px-3 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50" onClick={() => { setFilters(EMPTY); setSearched(false); setChecked({}); setResult([]); }}>
            {t('bulk:reset')}
          </button>
        </div>
        <div className="mt-2 text-[0.6875rem] text-gray-400">
          {scan.busy ? t('bulk:reading', { done: scan.done, total: scan.total }) : t('bulk:listHint')}
          {scan.error && <span className="text-red-600 ml-2">{t('bulk:scanError', { error: scan.error })}</span>}
        </div>
      </div>

      {/* ── 결과 */}
      <div className="bg-white rounded-lg shadow-md mb-4">
        <div className="p-3 border-b border-gray-200 flex items-center gap-2">
          <span className="font-medium">{t('bulk:results')}{searched ? t('bulk:resultCount', { n: rows.length }) : ''}</span>
          <span className="text-xs text-gray-500">{t('bulk:selectedCount', { n: selected.length })}</span>
          <span className="ml-auto flex gap-1">
            <button className="px-2 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-50" onClick={() => setChecked(Object.fromEntries(rows.map((r) => [r.key, true])))}>{t('bulk:selectAll')}</button>
            <button className="px-2 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-50" onClick={() => setChecked({})}>{t('bulk:clearSelection')}</button>
          </span>
        </div>
        <div className="max-h-[360px] overflow-auto">
          {!searched ? (
            <div className="p-6 text-center text-sm text-gray-500">{t('bulk:pressSearch')}</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">{t('bulk:noMatch')}</div>
          ) : (
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-gray-500">
                  <th className="px-2 py-1 w-8"> </th>
                  <th className="px-2 py-1">{t('bulk:colWorkflow')}</th>
                  <th className="px-2 py-1">{t('bulk:colNode')}</th>
                  <th className="px-2 py-1">{t('bulk:attr')}</th>
                  <th className="px-2 py-1">{t('bulk:colCurrent')}</th>
                  <th className="px-2 py-1">{t('bulk:colNext')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const on = !!checked[r.key];
                  const next = on ? nextValueOf(r) : undefined;
                  const changed = on && String(next) !== String(r.value);
                  return (
                    <tr key={r.key} className={`border-b border-gray-100 ${on ? 'bg-blue-50/40' : ''}`}>
                      <td className="px-2 py-1">
                        <input type="checkbox" checked={on} onChange={(e) => setChecked((p) => ({ ...p, [r.key]: e.target.checked }))} />
                      </td>
                      <td className="px-2 py-1 text-gray-700">
                        <span className="text-gray-400">{multi ? `${r.projectName} / ` : ''}{r.workflow.folder ? `${r.workflow.folder} / ` : ''}</span>{r.workflow.name}
                      </td>
                      <td className="px-2 py-1 text-gray-700">{r.nodeLabel} <span className="text-gray-400">{typeLabel(r.nodeType)}</span></td>
                      <td className="px-2 py-1 font-mono text-gray-600">{r.attr}</td>
                      <td className="px-2 py-1 font-mono text-gray-800 truncate max-w-[260px]" title={String(r.value ?? '')}>{String(r.value ?? '')}</td>
                      <td className={`px-2 py-1 font-mono truncate max-w-[260px] ${changed ? 'text-blue-700' : 'text-gray-300'}`} title={next === undefined ? '' : String(next)}>
                        {changed ? String(next) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── 일괄 수정 — 라디오·입력칸을 가운데 높이로 맞춘다 */}
      <div className="bg-white rounded-lg shadow-md p-3">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1 text-xs text-gray-700">
            <input type="radio" checked={editMode === 'set'} onChange={() => setEditMode('set')} /> {t('bulk:setValue')}
          </label>
          {filters.attr === 'connection' ? (
            <select className={inputCls} value={newValue} disabled={editMode !== 'set'} onChange={(e) => setNewValue(e.target.value)}>
              <option value="">{t('bulk:selectConnection')}</option>
              {connections.map((c: any) => <option key={c.connection_id} value={c.connection_id}>{c.connection_id}</option>)}
            </select>
          ) : (
            <input className={inputCls} value={newValue} disabled={editMode !== 'set'} onChange={(e) => setNewValue(e.target.value)} placeholder={t('bulk:newValuePh')} />
          )}

          <label className="flex items-center gap-1 text-xs text-gray-700 ml-4">
            <input type="radio" checked={editMode === 'replace'} onChange={() => setEditMode('replace')} /> {t('bulk:replace')}
          </label>
          <input className={inputCls} value={findText} disabled={editMode !== 'replace'} onChange={(e) => setFindText(e.target.value)} placeholder={t('bulk:findPh')} />
          <span className="text-xs text-gray-400">→</span>
          <input className={inputCls} value={replaceText} disabled={editMode !== 'replace'} onChange={(e) => setReplaceText(e.target.value)} placeholder={t('bulk:replacePh')} />

          <button
            className="ml-auto px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            disabled={viewOnly || saving || changes.length === 0}
            title={viewOnly ? t('bulk:viewOnly') : t('bulk:applyTitle')}
            onClick={applyAndSave}
          >
            {saving ? t('bulk:saving') : t('bulk:saveChanges', { n: changes.length })}
          </button>
        </div>
        <div className="mt-2 text-[0.6875rem] text-gray-500">
          {t('bulk:typeNote')}
        </div>
        {result.length > 0 && (
          <div className="mt-2 text-xs space-y-0.5">
            {result.map((l) => <div key={l} className={l.startsWith('✓') ? 'text-green-700' : 'text-red-700'}>{l}</div>)}
            <div className="text-gray-500">{t('bulk:redeploy')}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkEditScreen;
